import React, { useState } from "react";
import { Star, Heart, CheckCircle2, EyeIcon } from "lucide-react";
import { Button } from "../../shadcn/ui/Button";
import { Badge } from "../../shadcn/ui/Badge";
import { getProductDetailShareUrl } from "../../lib/productRoutes";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect fill='%23e5e7eb' width='400' height='400'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='18' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";

export interface EnhancedProductCardProps {
  product: {
    id: string;
    slug?: string;
    title: string;
    price: string;
    originalPrice?: string;
    moq: string;
    unitPrice?: string;
    score?: number;
    rating?: number;
    supplier: {
      id?: string;
      name: string;
      rating: number;
      verified: boolean;
      location?: string;
      locationFlag?: string;
    };
    image: string;
    badges?: string[];
  };
  isFavorited?: boolean;
  onFavoriteToggle?: (productId: string, isFavorited: boolean) => Promise<void>;
  onContactSupplier?: (productId: string, supplierId?: string) => void;
}

export const EnhancedProductCard: React.FC<EnhancedProductCardProps> = ({
  product,
  isFavorited = false,
  onFavoriteToggle,
  onContactSupplier,
}) => {
  const [favoriteState, setFavoriteState] = useState(isFavorited);
  const [isToggling, setIsToggling] = useState(false);

  const {
    id,
    slug,
    title,
    price,
    originalPrice,
    moq,
    unitPrice,
    score,
    rating,
    supplier,
    image,
    badges = [],
  } = product;

  const renderStars = (ratingValue: number) =>
    Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${
          i < Math.floor(ratingValue)
            ? "fill-yellow-400 text-yellow-400"
            : "text-gray-300"
        }`}
      />
    ));

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onFavoriteToggle || isToggling) return;

    setIsToggling(true);
    const newFavoriteState = !favoriteState;
    setFavoriteState(newFavoriteState);

    try {
      await onFavoriteToggle(id, newFavoriteState);
    } catch (error) {
      setFavoriteState(!newFavoriteState);
      console.error("Error toggling favorite:", error);
    } finally {
      setIsToggling(false);
    }
  };

  const openProductDetail = () => {
    const url = getProductDetailShareUrl({ id, slug });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleViewProduct = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openProductDetail();
  };

  const handleCardClick = () => {
    openProductDetail();
  };

  return (
    <div
      className="group min-w-0 bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg hover:border-[#F97316] transition-all duration-200 flex flex-col"
      onClick={handleCardClick}
    >
      {/* Image Section */}
      <div className="relative w-full aspect-square overflow-hidden bg-gray-100">
        <img
          src={image || PLACEHOLDER_IMAGE}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => {
            (e.target as HTMLImageElement).src = PLACEHOLDER_IMAGE;
          }}
        />

        {/* Badges Overlay */}
        {badges.length > 0 && (
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {badges.map((badge, index) => (
              <Badge
                key={index}
                className="bg-[#F97316] text-white text-xs px-2 py-0.5"
              >
                {badge}
              </Badge>
            ))}
          </div>
        )}

        {/* Favorite Icon */}
        {onFavoriteToggle && (
          <button
            onClick={handleFavoriteClick}
            disabled={isToggling}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-white/95 hover:bg-white shadow-md transition-all z-10"
            aria-label={
              favoriteState ? "Remove from favorites" : "Add to favorites"
            }
          >
            <Heart
              className={`h-4 w-4 ${
                favoriteState
                  ? "fill-red-500 text-red-500"
                  : "text-gray-600 hover:text-red-500"
              }`}
            />
          </button>
        )}

        {/* Score Badge */}
        {/* {(score || rating) && (
          <div className="absolute bottom-2 right-2 bg-[#F97316] text-white px-2 py-1 rounded text-xs font-semibold shadow-md">
            {(score || rating || 0).toFixed(1)}/5
          </div>
        )} */}

      </div>

      {/* Product Info — min-w-0 + break-words so line-clamp matches /search grid cards (no third-line bleed) */}
      <div className="min-w-0 flex flex-1 flex-col p-3">
        <h3
          title={title}
          className="mb-2 line-clamp-2 min-h-[2.5rem] max-h-[2.5rem] overflow-hidden break-words text-sm font-semibold leading-[1.25rem] text-gray-900 group-hover:text-[#F97316] transition-colors"
        >
          {title}
        </h3>

        {/* Price Section */}
        <div className="mb-2">
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold text-[#F97316]">{price}</span>
            {originalPrice && (
              <span className="text-sm text-gray-500 line-through">
                {originalPrice}
              </span>
            )}
          </div>
          {unitPrice ? (
            <p className="mt-0.5 text-xs text-gray-600">{unitPrice}</p>
          ) : null}
        </div>

        {/* MOQ */}
        <div className="text-xs text-gray-600 mb-2">MOQ: {moq}</div>

        {/* Supplier Info */}
        <div className="mt-auto pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <span className="text-xs text-gray-500 truncate">
                {supplier.locationFlag && (
                  <span className="mr-1">{supplier.locationFlag}</span>
                )}
                {supplier.name}
              </span>
              {supplier.verified && (
                <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" />
              )}
            </div>
            {/* <div className="flex items-center gap-0.5 flex-shrink-0">
              {renderStars(supplier.rating)}
              <span className="text-xs text-gray-500 ml-1">
                {supplier.rating.toFixed(1)}
              </span>
            </div> */}
          </div>

          <div className="max-h-0 opacity-0 overflow-hidden pointer-events-none group-hover:max-h-12 group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-200">
            <Button
              onClick={handleViewProduct}
              className="w-full h-8 mt-2 text-xs bg-[#F97316] hover:bg-[#ea6820] text-white"
              size="sm"
            >
              <EyeIcon className="h-3 w-3 mr-1" />
              View Product
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
